import { useEffect, useId, useState, type FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCommentDots, faDiagramProject, faPause, faPen, faPlay, faPlus, faRotateRight, faTrash } from '@fortawesome/free-solid-svg-icons';
import { fastapi } from '../../services/api';
import { GhostSelect } from '../../components/ui/GhostSelect';
import BtnTaratic from '../../components/ui/BtnTaratic';
import StatusLabel, { type StatusLabelVariant } from '../../components/ui/StatusLabel';
import StatusNotice from '../../components/ui/StatusNotice';
import DefaultCard from '../../components/ui/DefaultCard';
import AppModal from '../../components/ui/AppModal';
import BehaviorInfo from '../../components/ui/BehaviorInfo';
import './workflows.css';

type WorkflowTrigger = 'comment' | 'dm';
type Workflow = {
  id?: number;
  name: string;
  channel: 'instagram';
  trigger?: WorkflowTrigger;
  enabled: boolean;
  keywords: string[];
  media_ids: string[];
  message: string;
};
type Delivery = { account_id: string; comment_id: string; workflow_id: number; status: string; created_at: string };

const blankWorkflow = (): Workflow => ({ name: '', channel: 'instagram', trigger: 'comment', enabled: false, keywords: [], media_ids: [], message: '' });
const normalizeWorkflow = (workflow: Workflow): Workflow => ({ ...workflow, trigger: workflow.trigger || 'comment', keywords: workflow.keywords || [], media_ids: workflow.media_ids || [] });
const triggerLabel = (trigger?: WorkflowTrigger) => trigger === 'dm' ? 'DM por palabra clave' : 'Comentario → DM';
const statusLabels: Record<string, { label: string; variant: StatusLabelVariant }> = {
  sent: { label: 'Enviado', variant: 'success' },
  failed: { label: 'Rechazado por Instagram', variant: 'danger' },
  uncertain: { label: 'Sin confirmar', variant: 'warning' },
  sending: { label: 'En curso', variant: 'info' },
  rate_limited: { label: 'Limitado por Instagram', variant: 'warning' },
};

export default function CommentAutomations() {
  const [items, setItems] = useState<Workflow[]>([]);
  const [templates, setTemplates] = useState<Workflow[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [draft, setDraft] = useState<Workflow | null>(null);
  const [deleting, setDeleting] = useState<Workflow | null>(null);
  const [keywords, setKeywords] = useState('');
  const [media, setMedia] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const formId = useId();

  const load = async () => {
    const [workflows, presets, history] = await Promise.all([
      fastapi.get('/automations'), fastapi.get('/automations/templates'), fastapi.get('/automations/deliveries/recent'),
    ]);
    setItems(workflows.data.map(normalizeWorkflow));
    setTemplates(presets.data.map(normalizeWorkflow));
    setDeliveries(history.data);
  };
  useEffect(() => {
    void load().catch(() => setError('No se pudieron cargar los workflows.')).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    setBusy(true);
    setError('');
    try { await load(); } catch { setError('No se pudo actualizar. Inténtalo de nuevo.'); } finally { setBusy(false); }
  };
  const edit = (value: Workflow) => {
    const normalized = normalizeWorkflow(value);
    setDraft({ ...normalized, keywords: [...normalized.keywords], media_ids: [...normalized.media_ids] });
    setKeywords(normalized.keywords.join(', '));
    setMedia(normalized.media_ids.join(', '));
    setError('');
    setNotice('');
  };
  const closeEditor = () => { if (!busy) { setDraft(null); setError(''); } };
  const payloadFor = (workflow: Workflow) => {
    const { id: _id, ...payload } = workflow;
    return payload;
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    const split = (value: string) => value.split(',').map(part => part.trim()).filter(Boolean);
    const workflow = normalizeWorkflow({ ...draft, keywords: split(keywords), media_ids: draft.trigger === 'dm' ? [] : split(media) });
    try {
      if (draft.id) await fastapi.put(`/automations/${draft.id}`, payloadFor(workflow));
      else await fastapi.post('/automations', payloadFor(workflow));
    } catch {
      setError('No se pudo guardar. Revisa las palabras, el mensaje y los ID numéricos.');
      setBusy(false);
      return;
    }
    setDraft(null);
    setNotice('Workflow guardado.');
    try { await load(); } catch { setError('El workflow se guardó, pero no se pudo actualizar la lista. Pulsa Actualizar.'); } finally { setBusy(false); }
  };
  const toggleStatus = async (item: Workflow) => {
    if (!item.id || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fastapi.put(`/automations/${item.id}`, payloadFor({ ...item, enabled: !item.enabled }));
      setItems(current => current.map(value => value.id === item.id ? { ...value, enabled: !item.enabled } : value));
      setNotice(item.enabled ? 'Workflow pausado.' : 'Workflow activado.');
    } catch { setError('No se pudo cambiar el estado del workflow.'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!deleting?.id || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fastapi.delete(`/automations/${deleting.id}`);
      setItems(current => current.filter(item => item.id !== deleting.id));
      setDeleting(null);
      setNotice('Workflow eliminado.');
    } catch { setError('No se pudo eliminar el workflow. Inténtalo de nuevo.'); } finally { setBusy(false); }
  };
  const activeCount = items.filter(item => item.enabled).length;

  return (
    <section data-ui="operator-workflows" className="workflows-page page-theme-surface">
      <header className="workflows-header">
        <div>
          <p className="workflows-eyebrow">INSTAGRAM · AUTOMATIZACIONES</p>
          <div className="workflows-heading">
            <h1>Workflows</h1>
            <BehaviorInfo label="Cómo funcionan los workflows" title="Una palabra, una respuesta">
              <p>Envía un mensaje privado cuando alguien use una de tus palabras clave en un comentario o DM. La respuesta es el texto que configures.</p>
              <p>Coinciden palabras o frases completas, sin distinguir mayúsculas ni acentos. Si varios workflows coinciden, se ejecuta el primero por orden de creación.</p>
              <p>Cada comentario o mensaje se procesa una sola vez. Los envíos sin confirmar no se repiten automáticamente.</p>
              <p>En DM, se respeta el bot pausado, la derivación a una persona y la ventana de respuesta. Estas comprobaciones del chat no se aplican al disparador de comentarios.</p>
              <p>Los workflows tienen su propia configuración; no generan respuestas con IA. Para comentarios, reconecta Instagram desde Conexiones y acepta el permiso para gestionar comentarios si aún no lo has concedido.</p>
            </BehaviorInfo>
          </div>
          <p className="workflows-subtitle">Convierte palabras clave en conversaciones.</p>
        </div>
        <BtnTaratic color="blue" type="button" size="md" onClick={() => edit(blankWorkflow())} disabled={busy || loading}>
          <FontAwesomeIcon icon={faPlus} aria-hidden="true" className="mr-2" />Nuevo workflow
        </BtnTaratic>
      </header>

      {error && !draft && !deleting && <StatusNotice role="alert" variant="danger">{error}</StatusNotice>}
      {notice && <StatusNotice role="status" variant="success">{notice}</StatusNotice>}

      <DefaultCard className="workflows-overview" aria-label="Resumen de workflows" aria-busy={loading}>
        <div><span className="workflows-count">{loading ? '—' : items.length}</span><span>Configurados</span></div>
        <div><span className="workflows-count workflows-count-active">{loading ? '—' : activeCount}</span><span>Activos</span></div>
        <div><span className="workflows-count">{loading ? '—' : items.length - activeCount}</span><span>Pausados</span></div>
      </DefaultCard>

      {templates.length > 0 && <DefaultCard as="section" aria-labelledby="workflow-templates-title" className="workflows-templates">
        <div><h2 id="workflow-templates-title">Empieza con una plantilla</h2><p>Personalízala y actívala cuando esté lista.</p></div>
        <div className="workflows-template-actions" aria-label="Plantillas">
          {templates.map(template => <BtnTaratic key={`${template.trigger}:${template.name}`} color="outline" onClick={() => edit(template)} disabled={busy}>
            {template.name}<FontAwesomeIcon icon={faArrowRight} aria-hidden="true" className="ml-2" />
          </BtnTaratic>)}
        </div>
      </DefaultCard>}

      <section aria-labelledby="workflows-title" className="workflows-section">
        <div className="workflows-section-heading">
          <div className="workflows-heading"><h2 id="workflows-title">Tus workflows</h2>
            <BehaviorInfo label="Orden de ejecución" title="El primero que coincide">
              <p>Los workflows se evalúan por orden de creación. Si una palabra activa varios, solo responde el primero que esté activo y coincida con el disparador y la publicación.</p>
              <p>Las plantillas se guardan pausadas salvo que marques «Activar workflow al guardar».</p>
            </BehaviorInfo>
          </div>
          <span className="workflows-muted">Por orden de creación</span>
        </div>
        {loading ? <div className="workflows-empty" role="status">Cargando workflows…</div> : !items.length ? (
          <DefaultCard className="workflows-empty">
            <span className="workflows-empty-icon"><FontAwesomeIcon icon={faDiagramProject} aria-hidden="true" /></span>
            <h3>Tu primera conversación empieza aquí</h3>
            <p>Todavía no hay workflows. Elige una plantilla o crea uno con tus propias palabras.</p>
          </DefaultCard>
        ) : <div className="workflows-grid">{items.map(item => (
          <DefaultCard as="article" key={item.id} className="workflow-card" aria-label={item.name}>
            <div className="workflow-card-heading">
              <div className="workflow-card-title"><span className="workflow-trigger-icon"><FontAwesomeIcon icon={faCommentDots} aria-hidden="true" /></span><div><h3>{item.name}</h3><p>{triggerLabel(item.trigger)}</p></div></div>
              <StatusLabel variant={item.enabled ? 'success' : 'neutral'}>{item.enabled ? 'Activo' : 'Pausado'}</StatusLabel>
            </div>
            <div className="workflow-keywords" aria-label="Palabras clave">{item.keywords.map(keyword => <StatusLabel key={keyword} variant="info">{keyword}</StatusLabel>)}</div>
            <div className="workflow-response"><span>Respuesta privada</span><p>{item.message}</p></div>
            <p className="workflow-scope">{item.trigger === 'dm' ? 'Mensajes directos' : item.media_ids.length ? `${item.media_ids.length} publicación${item.media_ids.length === 1 ? '' : 'es'} seleccionada${item.media_ids.length === 1 ? '' : 's'}` : 'Todas las publicaciones y reels'}</p>
            <div className="workflow-card-actions">
              <BtnTaratic type="button" disabled={busy} onClick={() => edit(item)}><FontAwesomeIcon icon={faPen} aria-hidden="true" className="mr-2" />Editar</BtnTaratic>
              <BtnTaratic type="button" color="outline" disabled={busy} onClick={() => void toggleStatus(item)}><FontAwesomeIcon icon={item.enabled ? faPause : faPlay} aria-hidden="true" className="mr-2" />{item.enabled ? 'Pausar' : 'Activar'}</BtnTaratic>
              <BtnTaratic type="button" color="outline" iconOnly disabled={busy} onClick={() => { setDeleting(item); setError(''); }} aria-label={`Eliminar ${item.name}`} className="workflow-delete"><FontAwesomeIcon icon={faTrash} aria-hidden="true" /></BtnTaratic>
            </div>
          </DefaultCard>
        ))}</div>}
      </section>

      <DefaultCard as="section" aria-labelledby="deliveries-title" className="workflows-history">
        <div className="workflows-section-heading">
          <div><div className="workflows-heading"><h2 id="deliveries-title">Últimas ejecuciones</h2>
            <BehaviorInfo label="Información sobre las ejecuciones" title="Comprueba cada envío">
              <p>Se muestran las últimas 50 ejecuciones. Pulsa Actualizar para consultar nuevos resultados.</p>
              <p>«Enviado» indica que Instagram aceptó el mensaje; comprueba también la bandeja del destinatario. «Sin confirmar» no garantiza que haya fallado: evita repetir el envío sin comprobarlo.</p>
              <p>Los envíos sin confirmar no se repiten automáticamente para evitar duplicados.</p>
            </BehaviorInfo></div><p className="workflows-muted">El resultado de tus respuestas automáticas.</p></div>
          <BtnTaratic type="button" color="outline" loading={busy && !draft && !deleting} disabled={loading} onClick={() => void refresh()}><FontAwesomeIcon icon={faRotateRight} aria-hidden="true" className="mr-2" />Actualizar</BtnTaratic>
        </div>
        {loading ? <p className="workflows-empty" role="status">Cargando ejecuciones…</p> : !deliveries.length ? <div className="workflows-history-empty"><FontAwesomeIcon icon={faCommentDots} aria-hidden="true" /><p>Todavía no hay ejecuciones.</p><span>Cuando se active una palabra clave, verás el resultado aquí.</span></div> : (
          <div className="workflows-table-wrap"><table className="workflows-table"><caption className="sr-only">Últimas ejecuciones de workflows</caption>
            <thead><tr><th scope="col">Workflow</th><th scope="col">Fecha</th><th scope="col">Estado</th></tr></thead>
            <tbody>{deliveries.map(delivery => { const status = statusLabels[delivery.status]; return (
              <tr key={`${delivery.account_id}:${delivery.comment_id}`}><td data-label="Workflow">{items.find(item => item.id === delivery.workflow_id)?.name || `Workflow #${delivery.workflow_id}`}</td><td data-label="Fecha"><time dateTime={delivery.created_at}>{new Date(delivery.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</time></td><td data-label="Estado"><StatusLabel variant={status?.variant || 'neutral'}>{status?.label || delivery.status}</StatusLabel></td></tr>
            ); })}</tbody>
          </table></div>
        )}
      </DefaultCard>

      <AppModal open={!!draft} onClose={closeEditor} title={draft?.id ? 'Editar workflow' : 'Nuevo workflow'} footer={<><BtnTaratic type="button" color="ghost-dark" disabled={busy} onClick={closeEditor}>Cancelar</BtnTaratic><BtnTaratic color="green" type="submit" form={formId} loading={busy}>Guardar workflow</BtnTaratic></>}>
        {draft && <form id={formId} onSubmit={save} className="workflow-editor" aria-label={draft.id ? `Editar ${draft.name}` : 'Crear workflow'}>
          {error && <StatusNotice role="alert" variant="danger">{error}</StatusNotice>}
          <fieldset disabled={busy}>
            <div className="workflow-editor-intro"><StatusLabel variant="info">Instagram</StatusLabel><span>Define cuándo responder y qué decir.</span></div>
            <div className="workflow-form-grid">
              <label>Nombre<input autoFocus aria-label="Nombre" required maxLength={100} value={draft.name} placeholder="Ej. Información de productos" onChange={event => setDraft({ ...draft, name: event.target.value })} /></label>
              <div className="workflow-field" role="group" aria-labelledby={`${formId}-trigger`}><span id={`${formId}-trigger`}>Disparador</span><GhostSelect ariaLabel="Disparador" disabled={busy} value={draft.trigger || 'comment'} options={[{ value: 'comment', label: 'Comentario → DM' }, { value: 'dm', label: 'DM por palabra clave' }]} onChange={value => { const trigger = value as WorkflowTrigger; setDraft({ ...draft, trigger, media_ids: trigger === 'dm' ? [] : draft.media_ids }); if (trigger === 'dm') setMedia(''); }} /></div>
            </div>
            <div className="workflow-field">
              <div className="workflow-field-heading"><label htmlFor={`${formId}-keywords`}>Palabras clave</label><BehaviorInfo label="Cómo coinciden las palabras clave" title="Palabras completas, sin acentos"><p>Separa las palabras o frases por comas. Puedes añadir hasta 20, de un máximo de 80 caracteres cada una.</p><p>«¡CÍTA!» coincide con «cita»; «felicitar» no. Una sola coincidencia basta para activar el workflow.</p></BehaviorInfo></div>
              <input id={`${formId}-keywords`} aria-label="Palabras clave, separadas por comas" required value={keywords} placeholder="INFO, precio, más información" onChange={event => setKeywords(event.target.value)} />
              <p className="workflow-field-hint">Separa cada palabra o frase con una coma.</p>
            </div>
            {draft.trigger !== 'dm' && <div className="workflow-field">
              <div className="workflow-field-heading"><label htmlFor={`${formId}-media`}>Publicaciones <span className="workflows-muted">· opcional</span></label><BehaviorInfo label="Elegir publicaciones" title="Dónde se activa"><p>Déjalo vacío para todas las publicaciones y reels, o introduce sus ID numéricos separados por comas. No uses enlaces de Instagram.</p><p>No se responde a comentarios de tu propia cuenta ni de directos.</p></BehaviorInfo></div>
              <input id={`${formId}-media`} aria-label="ID de publicaciones o reels (opcional)" value={media} onChange={event => setMedia(event.target.value)} placeholder="Todas, o ID numéricos separados por comas" />
            </div>}
            <label>Mensaje privado<textarea aria-label="Mensaje privado" rows={4} required maxLength={1000} value={draft.message} placeholder="Escribe la respuesta que recibirá la persona…" onChange={event => setDraft({ ...draft, message: event.target.value })} /><span className="workflow-character-count">{draft.message.length}/1000</span></label>
            <label className="workflow-activation"><input aria-label="Activar workflow al guardar" type="checkbox" checked={draft.enabled} onChange={event => setDraft({ ...draft, enabled: event.target.checked })} /><span>Activar workflow al guardar<small>{draft.enabled ? 'Responderá cuando coincida una palabra clave.' : 'Se guardará pausado para que puedas revisarlo.'}</small></span></label>
          </fieldset>
        </form>}
      </AppModal>
      <AppModal open={!!deleting} onClose={() => { if (!busy) { setDeleting(null); setError(''); } }} title="Eliminar workflow" footer={<><BtnTaratic type="button" color="ghost-dark" disabled={busy} onClick={() => { setDeleting(null); setError(''); }}>Cancelar</BtnTaratic><BtnTaratic type="button" color="red" loading={busy} onClick={() => void remove()}>Eliminar workflow</BtnTaratic></>}>
        <div className="workflow-editor">{error && <StatusNotice role="alert" variant="danger">{error}</StatusNotice>}<p>¿Eliminar <strong>{deleting?.name}</strong>?</p><p className="workflows-muted">Dejará de responder a nuevas coincidencias. Su historial se conserva.</p></div>
      </AppModal>
    </section>
  );
}
